import { useCallback, useState } from 'react';
import { App, Button, Segmented, Select, Spin } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router';
import { TEMPLATE_DECISION_CLASSES, type TemplateDecisionClass, type TemplateDecisionRowDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import { can } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { TEMPLATE_DECISION_CLASS_LABEL, TEMPLATE_DECISION_CLASS_QUESTION } from '@/lib/labels';
import { queryKeys } from '@/lib/queryKeys';
import { DecisionSummary } from '../components/DecisionSummary';
import { DECISIONS_COPY } from '../copy';
import { useTemplateDecisions, useTemplateSet } from '../templateQueries';
import { DecisionRow } from './DecisionRow';
import { policyCovers, useDecisionMasters } from './useDecisionMasters';
import { useSaveDecision } from './useSaveDecision';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3] THE DECISIONS PAGE — ONE DECISION PER (SET, CLASS, CODE), AND EVERY TEMPLATE USING THE CODE
 *  INHERITS IT (exit criterion 5).
 *
 *  ⚠️ ONE `Segmented` PER CLASS, IN THE URL (`?class=`), so a link from the library header lands on the
 *  class it names. Rows that are still undecided come first; a decided one shows by whom, or that it was
 *  matched by code, and what no longer holds.
 *
 *  ⚠️ A SAVE INVALIDATES `templates.set(setKey)` — the set's summary, its templates' readiness and every
 *  class's list together, one deliberate act. It never reaches a PLAN: a plan is a sibling key and is
 *  worked out again only when somebody asks, so nobody's open plan changes under them.
 *
 *  ⚠️ THE POLICY CLASS HAS ONE BULK ACT: "use this policy for every set it covers" — only the column
 *  sets still undecided, and only those its CURRENT version carries in full, confirmed with the count
 *  first, then one PUT each (each its own decision and its own audit row, exactly as one by hand).
 *
 *  [amended P7C §4] THE SAVE IS `useSaveDecision` — this page's own mutation, moved verbatim so the
 *  library's preview saves the same way; this page's behaviour, and its suite, are unchanged.
 *
 *  [amended P7B.1] `?code=` OPENS ONE ROW'S FORM AND SCROLLS IT INTO VIEW — the library's "Decide" and the
 *  way back from the post type master land on the decision they are about. Read ONCE, as initial state:
 *  closing the form or choosing another class must not be overridden by a link the officer has already
 *  followed, and changing class drops the code from the URL.
 *
 *  [amended TEMPLATE-FIND] THIS PAGE IS NO LONGER BYTE-IDENTICAL TO [P7C §4] — the user asked how to find
 *  the template a decision names. Every template number a row shows links to it (`DecisionRow`), a printed
 *  total or a duty point says where it is printed and what to check, and links to that very check or row
 *  (`DecisionContext`). The page adds NO request: `LibraryIndex` resolves `?t=` from the list the library
 *  already reads, and the template pane scrolls its own target into view — the one `scrollIntoView` here is
 *  still `?code=`'s.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const isDecisionClass = (value: string | null): value is TemplateDecisionClass =>
  (TEMPLATE_DECISION_CLASSES as readonly (string | null)[]).includes(value);

const undecidedFirst = (rows: readonly TemplateDecisionRowDto[]): TemplateDecisionRowDto[] =>
  [...rows].sort((a, b) => Number(a.decision !== null) - Number(b.decision !== null));

const TemplateDecisionsPage = (): React.ReactElement => {
  const { setKey = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const set = useTemplateSet(setKey);

  const requested = searchParams.get('class');
  const firstOutstanding = set.data?.decisions.classes.find((row) => row.outstanding > 0)?.class;
  const decisionClass: TemplateDecisionClass = isDecisionClass(requested) ? requested : (firstOutstanding ?? 'SOURCE');

  const decisions = useTemplateDecisions(setKey, decisionClass);
  const masters = useDecisionMasters(
    {
      orgUnits: decisionClass === 'SOURCE',
      postTypes: decisionClass === 'FORCE_COLUMN',
      vehicleTypes: decisionClass === 'FORCE_COLUMN',
      policies: decisionClass === 'POLICY',
    },
    set.data?.days ?? [],
  );
  const [openCode, setOpenCode] = useState<string | null>(() => searchParams.get('code'));
  /* The row a link asked for, and the one ref that scrolls it — stable, so it runs once, on mount. */
  const [linkedCode] = useState<string | null>(() => searchParams.get('code'));
  const scrollToLinked = useCallback((node: HTMLLIElement | null) => {
    node?.scrollIntoView?.({ block: 'center' });
  }, []);
  const { officer } = useAuth();
  const mayCreatePostType = officer ? can(officer.accessRole, 'postType.write') : false;
  const [bulkPolicyId, setBulkPolicyId] = useState<string | undefined>();

  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: queryKeys.templates.set(setKey) });

  const save = useSaveDecision(setKey, () => {
    setOpenCode(null);
  });

  const bulk = useMutation({
    mutationFn: async (input: { policyId: string; codes: readonly string[] }): Promise<number> => {
      /* One at a time: each is its own decision and its own audit row, exactly as by hand. */
      for (const code of input.codes) {
        await api.put(`/templates/sets/${setKey}/decisions`, {
          class: 'POLICY',
          code,
          decision: { choice: 'POLICY', policyId: input.policyId },
        });
      }
      return input.codes.length;
    },
    onSuccess: (count) => {
      void message.success(DECISIONS_COPY.bulkDone(count));
      invalidate();
    },
    onError: (error: ApiError) => {
      void message.error(error.message);
      invalidate();
    },
  });

  if (set.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  if (!set.data) return <EmptyState title={DECISIONS_COPY.empty} />;

  const rows = undecidedFirst(decisions.data?.items ?? []);

  const applyBulk = (): void => {
    const policy = masters.policies.find((row) => row.id === bulkPolicyId);
    if (!policy) return;
    const covered = rows.filter(
      (row) => row.decision === null && row.context.class === 'POLICY' && policyCovers(policy, row.context.columns),
    );
    if (covered.length === 0) {
      void message.info(DECISIONS_COPY.bulkNone);
      return;
    }
    modal.confirm({
      title: DECISIONS_COPY.bulkConfirmTitle(policy.name, covered.length),
      content: <span className="text-sm">{DECISIONS_COPY.bulkConfirmBody}</span>,
      okText: DECISIONS_COPY.bulkApply,
      onOk: () => {
        bulk.mutate({ policyId: policy.id, codes: covered.map((row) => row.code) });
      },
    });
  };

  const counts = new Map(set.data.decisions.classes.map((row) => [row.class, row.outstanding]));

  return (
    <div className="flex flex-col gap-4" data-testid="template-decisions">
      <PageHeader
        icon={ListChecks}
        parent={{ label: DECISIONS_COPY.parent, to: `/templates/${setKey}` }}
        title={`${DECISIONS_COPY.title} · ${set.data.title}`}
        filters={
          <Segmented
            data-testid="decision-class"
            value={decisionClass}
            onChange={(value) => {
              setOpenCode(null);
              setSearchParams((current) => {
                current.set('class', String(value));
                current.delete('code');
                return current;
              });
            }}
            options={TEMPLATE_DECISION_CLASSES.map((each) => {
              const outstanding = counts.get(each) ?? 0;
              return {
                value: each,
                label:
                  outstanding > 0
                    ? `${TEMPLATE_DECISION_CLASS_LABEL[each]} (${String(outstanding)})`
                    : TEMPLATE_DECISION_CLASS_LABEL[each],
              };
            })}
          />
        }
      />

      <DecisionSummary summary={set.data.decisions} setKey={setKey} />

      <p className="text-sm text-muted-foreground" data-testid="decision-question">
        {TEMPLATE_DECISION_CLASS_QUESTION[decisionClass]}
      </p>

      {decisionClass === 'POLICY' ? (
        <section
          className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm"
          data-testid="policy-bulk"
          aria-label={DECISIONS_COPY.bulkTitle}
        >
          <label className="flex min-w-64 flex-col gap-1">
            <span className="text-muted-foreground">{DECISIONS_COPY.bulkPick}</span>
            <Select
              value={bulkPolicyId}
              onChange={setBulkPolicyId}
              options={masters.policies.map((policy) => ({ value: policy.id, label: policy.name }))}
              showSearch
              optionFilterProp="label"
              data-testid="policy-bulk-select"
            />
          </label>
          <Button
            disabled={bulkPolicyId === undefined}
            loading={bulk.isPending}
            onClick={applyBulk}
            data-testid="policy-bulk-apply"
          >
            {DECISIONS_COPY.bulkApply}
          </Button>
        </section>
      ) : null}

      {decisions.isPending ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={DECISIONS_COPY.empty} />
      ) : (
        <ul className="flex flex-col gap-3" data-testid="decision-rows">
          {rows.map((row) => (
            <DecisionRow
              key={row.code}
              row={row}
              setKey={setKey}
              scrollRef={row.code === linkedCode ? scrollToLinked : undefined}
              lookups={masters.lookups}
              open={openCode === row.code}
              onOpen={() => {
                setOpenCode(row.code);
              }}
              onClose={() => {
                setOpenCode(null);
              }}
              saving={save.isPending && save.variables.code === row.code}
              onSave={(body) => {
                save.mutate(body);
              }}
              form={{
                setKey,
                days: set.data.days,
                postTypes: masters.postTypes,
                vehicleTypes: masters.vehicleTypes,
                policies: masters.policies,
                unallocatableUnitIds: masters.unallocatable,
                mayCreatePostType,
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default TemplateDecisionsPage;
